import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authService, { COGNITO_ENABLED } from '../services/authService';

/**
 * Authentication store using Zustand with persistence.
 * Supports both Cognito (production) and legacy JWT (fallback).
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,      // Legacy only
      refreshToken: null,     // Legacy only
      isAuthenticated: false,
      isLoading: false,       // Don't start as loading - causes UI issues
      isInitialized: false,   // Track if we've checked auth
      error: null,

      /**
       * Initialize auth state on app load.
       * Checks if user is already authenticated.
       */
      initialize: async () => {
        // Skip if already initialized
        if (get().isInitialized) return;

        set({ isLoading: true, error: null });

        try {
          if (COGNITO_ENABLED) {
            // Check Cognito session
            const user = await authService.getCurrentUser();
            if (user) {
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
              });
            } else {
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isInitialized: true,
              });
            }
          } else {
            // Legacy: Check if we have stored tokens
            const { accessToken } = get();
            if (accessToken) {
              try {
                const user = await authService.getCurrentUser();
                set({
                  user,
                  isAuthenticated: true,
                  isLoading: false,
                  isInitialized: true,
                });
              } catch {
                // Token invalid, clear auth
                set({
                  user: null,
                  accessToken: null,
                  refreshToken: null,
                  isAuthenticated: false,
                  isLoading: false,
                  isInitialized: true,
                });
              }
            } else {
              set({ isLoading: false, isInitialized: true });
            }
          }
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            error: null,
          });
        }
      },

      /**
       * Login with email/password
       */
      login: async (email, password) => {
        set({ isLoading: true, error: null });

        try {
          if (COGNITO_ENABLED) {
            await authService.login(email, password);
            const user = await authService.getCurrentUser();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // Legacy login
            const response = await authService.login(email, password);
            const { access, refresh, user } = response;
            set({
              user,
              accessToken: access,
              refreshToken: refresh,
              isAuthenticated: true,
              isLoading: false,
            });
          }
          return { success: true };
        } catch (error) {
          const message = error.message === 'PENDING_APPROVAL'
            ? 'Votre compte est en attente d\'approbation'
            : error.message || 'Échec de la connexion';
          set({
            error: message,
            isLoading: false,
          });
          return { success: false, error: message };
        }
      },

      /**
       * Login with Google SSO
       */
      loginWithGoogle: async () => {
        set({ isLoading: true, error: null });
        try {
          await authService.loginWithGoogle();
          // User will be redirected, so we don't need to update state here
        } catch (error) {
          set({
            error: error.message || 'Échec de la connexion Google',
            isLoading: false,
          });
        }
      },

      /**
       * Register new user
       */
      register: async (data) => {
        set({ isLoading: true, error: null });

        try {
          await authService.register(data);
          set({ isLoading: false });
          return { success: true, needsConfirmation: COGNITO_ENABLED };
        } catch (error) {
          set({
            error: error.message || 'Échec de l\'inscription',
            isLoading: false,
          });
          return { success: false, error: error.message };
        }
      },

      /**
       * Confirm signup with verification code (Cognito only)
       */
      confirmSignUp: async (email, code) => {
        set({ isLoading: true, error: null });

        try {
          await authService.confirmSignUp(email, code);
          set({ isLoading: false });
          return { success: true };
        } catch (error) {
          set({
            error: error.message || 'Code de vérification invalide',
            isLoading: false,
          });
          return { success: false, error: error.message };
        }
      },

      /**
       * Logout
       */
      logout: async () => {
        try {
          if (COGNITO_ENABLED) {
            await authService.logout();
          } else {
            const { refreshToken } = get();
            await authService.logout(refreshToken);
          }
        } catch {
          // Ignore errors on logout
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
      },

      // Legacy actions (for backwards compatibility)
      setAuth: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          error: null,
        }),

      setUser: (user) =>
        set({ user }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setLoading: (isLoading) =>
        set({ isLoading }),

      setError: (error) =>
        set({ error }),

      clearError: () =>
        set({ error: null }),

      // Computed / Getters
      // Platform-level admin: superadmin only (project owners are NOT platform admins —
      // they manage their own project, not the whole MyMDO instance).
      isAdmin: () => {
        const { user } = get();
        return user?.role === 'superadmin' ||
               user?.role === 'admin' ||
               user?.profile?.role === 'admin' ||
               user?.is_superuser;
      },

      isSuperadmin: () => {
        const { user } = get();
        return user?.role === 'superadmin';
      },

      isApproved: () => {
        const { user } = get();
        return user?.status === 'active' ||
               user?.profile?.is_approved ||
               user?.is_superuser;
      },

      getTenantId: () => {
        const { user } = get();
        return user?.tenantId;
      },

      /**
       * Check approval status by forcing a token refresh.
       * Used on PendingApprovalPage to auto-redirect when approved.
       * Returns { approved: boolean, user: object|null }
       */
      checkApprovalStatus: async () => {
        if (!COGNITO_ENABLED) {
          return { approved: false, user: null };
        }

        try {
          // Force refresh the Cognito token to get updated claims
          const { fetchAuthSession } = await import('aws-amplify/auth');
          await fetchAuthSession({ forceRefresh: true });

          // Get updated user info with new claims
          const user = await authService.getCurrentUser();

          if (user && user.status === 'active') {
            // User is now approved - update store
            set({
              user,
              isAuthenticated: true,
            });
            return { approved: true, user };
          }

          return { approved: false, user };
        } catch {
          return { approved: false, user: null };
        }
      },
    }),
    {
      name: 'mymdo-auth-storage',
      partialize: (state) => ({
        // Only persist tokens for legacy - Cognito manages its own session
        accessToken: state.accessToken,      // Legacy
        refreshToken: state.refreshToken,    // Legacy
        // Don't persist user, isAuthenticated - let initialize() check fresh
      }),
    }
  )
);

export default useAuthStore;
